import { useMutation, useQuery } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import Input from "src/components/Input";
import InputPhone from "src/components/InputPhone";
import { CreateAsoebiGroup } from "src/graphql/mutations";
import { GetAsoEbi, GetProfileDetails } from "src/graphql/queries";
import useAppStore from "src/store/utils";
import { user } from "src/types/api";
import { CreateAseobi, CreateAseobiVariables } from "src/utils/gql_types";
import * as Yup from "yup";
import styles from "./styles.module.scss";

interface Values {
  group_name: string;
  phoneNumber: string;
}

const AddPartSchema = Yup.object().shape({
  group_name: Yup.string().required("Group name is required"),
});

interface Props {
  setOpen: Dispatch<SetStateAction<boolean>>;
}

function CreateGroup({ setOpen }: Props) {
  const { data: profile } = useQuery<user>(GetProfileDetails);

  const [phoneNumber, setPhoneNumber] = useState<string>("");

  useEffect(() => {
    setPhoneNumber(profile?.user?.phone_number || "");
  }, [profile?.user?.phone_number]);

  const navigate = useNavigate();

  const { event } = useAppStore((state) => state);

  const [createAsoebi, { loading }] = useMutation<
    CreateAseobi,
    CreateAseobiVariables
  >(CreateAsoebiGroup, {
    onCompleted(data) {
      setOpen(false);
      toast.success(
        <p className="toast">You have successfully created a group.</p>
      );
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occurred"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
    refetchQueries: [
      {
        query: GetAsoEbi,
        variables: {
          event_id: event.uuid,
        },
      },
    ],
  });

  return (
    <div className={styles["AddPart"]}>
      <div className={styles["AddPart-header"]}>
        <h1>Create group</h1>
        <span>
          A name that represents the specific people you're curating for.
        </span>
      </div>
      <Formik
        initialValues={{
          group_name: "",
          phoneNumber: "",
        }}
        validationSchema={AddPartSchema}
        onSubmit={(values) => {
          createAsoebi({
            variables: {
              event_id: event.uuid,
              name: values.group_name,
            },
          });
        }}
      >
        {(props: FormikProps<Values>) => (
          <Form className={styles["AddPart-form"]}>
            <div className={styles["AddPart-form_container"]}>
              <Input
                type="text"
                name="group_name"
                label="Name"
                placeholder="e.g Groom's family Aso-ebi"
              />
              <InputPhone
                name="contact_number"
                label="Contact number (Whatsapp)"
                placeholder="Add a contact number for guest inquiries after purchase."
                value={phoneNumber}
                setValue={setPhoneNumber}
              />
            </div>
            <div className={styles["AddPart-btnContainer"]}>
              <div>
                <Button
                  type="submit"
                  label="Create"
                  loader={loading}
                  disabled={props.values.group_name === "" || !props.isValid}
                >
                  Create
                </Button>
                <Button
                  type="reset"
                  onClick={() => {
                    props.resetForm();
                    setPhoneNumber(""); // manually reset phone input
                    setOpen(false);
                  }}
                  label="Cancel"
                  design="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default CreateGroup;
